import React from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { XMarkIcon, CheckCircleIcon, RocketLaunchIcon, ClockIcon } from '@heroicons/react/24/outline';
import Button from '../common/Button';

interface CollaborationCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPostNow: () => void;
  onPostLater: () => void;
}

const CollaborationCompletionModal: React.FC<CollaborationCompletionModalProps> = ({
  isOpen,
  onClose,
  onPostNow,
  onPostLater
}) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[10001]">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800"
      >
        {/* Celebration Header */}
        <div className="relative h-48 bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
          </div>
          
          <div className="relative flex flex-col items-center text-center px-6">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mb-4 ring-4 ring-white/10">
              <CheckCircleIcon className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight">Collaboration Complete!</h2>
          </div>
          
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/10 hover:bg-black/20 rounded-full transition-colors group"
          >
            <XMarkIcon className="h-5 w-5 text-white/80 group-hover:text-white" />
          </button>
        </div>

        <div className="p-8 space-y-8">
          <div className="text-center space-y-3">
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
              Congratulations on completing your collaboration! Now, would you like to showcase your work as a project in your portfolio?
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* Option 1: Post Now */}
            <button
              onClick={onPostNow}
              className="flex items-center p-5 bg-teal-50 dark:bg-teal-900/20 border-2 border-teal-500/50 hover:border-teal-500 rounded-2xl transition-all duration-300 group text-left shadow-lg shadow-teal-500/5 hover:shadow-teal-500/10"
            >
              <div className="w-14 h-14 bg-teal-500 rounded-xl flex items-center justify-center mr-5 shadow-lg shadow-teal-500/30 group-hover:scale-110 transition-transform">
                <RocketLaunchIcon className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Post as Project Now</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Pre-fill details and add your team members instantly.</p>
              </div>
            </button>

            {/* Option 2: Post Later */}
            <button
              onClick={onPostLater}
              className="flex items-center p-5 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 rounded-2xl transition-all duration-300 group text-left"
            >
              <div className="w-14 h-14 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center mr-5 group-hover:scale-110 transition-transform">
                <ClockIcon className="h-8 w-8 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Post it Later</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Mark as complete now, and you can post the project anytime from the request list.</p>
              </div>
            </button>
          </div>
          
          <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-xl">
             <p className="text-xs text-amber-800 dark:text-amber-400 text-center flex items-center justify-center">
               <ClockIcon className="h-4 w-4 mr-2" />
               The chat will remain open for 7 more days for final coordination.
             </p>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default CollaborationCompletionModal;
